import { useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkMath from 'remark-math'
import remarkGfm from 'remark-gfm'
import rehypeKatex from 'rehype-katex'
import rehypeRaw from 'rehype-raw'
import 'katex/dist/katex.min.css'

// Inject rich-text styles once
const STYLE_ID = 'rich-text-styles'
function ensureStyles() {
  if (document.getElementById(STYLE_ID)) return
  const style = document.createElement('style')
  style.id = STYLE_ID
  style.textContent = `
    .rich-text { color: inherit; word-break: break-word; }
    .rich-text p { margin: 0.4em 0; }
    .rich-text p:first-child { margin-top: 0; }
    .rich-text p:last-child { margin-bottom: 0; }
    .rich-text h1, .rich-text h2, .rich-text h3,
    .rich-text h4, .rich-text h5, .rich-text h6 {
      margin: 0.6em 0 0.3em; font-weight: 700; line-height: 1.3;
    }
    .rich-text h4 { font-size: 1em; }
    .rich-text h3 { font-size: 1.1em; }
    .rich-text table {
      width: 100%; border-collapse: collapse; margin: 0.6em 0;
      font-size: 0.88em;
    }
    .rich-text th, .rich-text td {
      border: 1px solid var(--sat-border, #444);
      padding: 6px 10px; text-align: left;
    }
    .rich-text th {
      background: var(--sat-surface-dim, rgba(255,255,255,0.05));
      font-weight: 600;
    }
    .rich-text ul, .rich-text ol { margin: 0.4em 0; padding-left: 1.4em; }
    .rich-text li { margin: 0.15em 0; }
    .rich-text code {
      background: var(--sat-surface-dim, rgba(255,255,255,0.06));
      padding: 1px 4px; border-radius: 3px; font-size: 0.9em;
    }
    .rich-text blockquote {
      margin: 0.5em 0; padding: 4px 12px;
      border-left: 3px solid var(--sat-border, #555);
      color: var(--sat-text-muted, #aaa);
    }
    .rich-text strong { font-weight: 700; }
    .rich-text details {
      margin: 0.5em 0; padding: 8px 12px;
      border: 1px solid var(--sat-border, #444);
      border-radius: 6px;
      background: var(--sat-surface-dim, rgba(255,255,255,0.03));
    }
    .rich-text summary {
      cursor: pointer; font-weight: 600; padding: 4px 0;
      user-select: none;
    }
    .rich-text details[open] summary { margin-bottom: 6px; }
    .rich-text img {
      max-width: 100%; height: auto; display: block;
      margin: 1em auto; border-radius: 8px;
    }
    .rich-text .katex-error { color: inherit; }
    .rich-text--sm { font-size: 0.875rem; }
    .rich-text--md { font-size: 1rem; }
    .rich-text--lg { font-size: 1.125rem; }
  `
  document.head.appendChild(style)
}

function isUnderlineOpen(node: any) {
  return node?.type === 'html' && /^<u\s*>$/i.test(node.value?.trim() || '')
}

function isUnderlineClose(node: any) {
  return node?.type === 'html' && /^<\/u\s*>$/i.test(node.value?.trim() || '')
}

function transformUnderlineNodes(node: any): any {
  if (!node || !Array.isArray(node.children)) return node

  const nextChildren: any[] = []

  for (let index = 0; index < node.children.length; index += 1) {
    const child = transformUnderlineNodes(node.children[index])

    if (!isUnderlineOpen(child)) {
      nextChildren.push(child)
      continue
    }

    const underlinedChildren: any[] = []
    let cursor = index + 1

    while (cursor < node.children.length) {
      const candidate = transformUnderlineNodes(node.children[cursor])
      if (isUnderlineClose(candidate)) break
      underlinedChildren.push(candidate)
      cursor += 1
    }

    if (cursor < node.children.length && isUnderlineClose(node.children[cursor])) {
      nextChildren.push({
        type: 'emphasis',
        data: { hName: 'u' },
        children: underlinedChildren,
      })
      index = cursor
      continue
    }

    nextChildren.push(child)
    nextChildren.push(...underlinedChildren)
    index = cursor - 1
  }

  node.children = nextChildren
  return node
}

function remarkUnderline() {
  return (tree: any) => transformUnderlineNodes(tree)
}

interface RichTextProps {
  children: string | null | undefined
  size?: 'sm' | 'md' | 'lg'
  className?: string
}

/**
 * Renders markdown (tables, headers, bold, etc.) and LaTeX ($...$, $$...$$).
 * Use for question passages, prompts, choices, and explanations.
 */
export function RichText({ children, size = 'sm', className }: RichTextProps) {
  useEffect(ensureStyles, [])

  if (!children) return null

  return (
    <div className={`rich-text rich-text--${size} ${className || ''}`} style={{ lineHeight: 1.7 }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath, remarkUnderline]}
        rehypePlugins={[rehypeRaw, rehypeKatex]}
        urlTransform={(url) => url}
      >
        {children}
      </ReactMarkdown>
    </div>
  )
}
